// src/components/common/PublicLayout.tsx
//
// Shell for the signed-out surfaces (/browse, /teams/:id). Those routes sit
// outside the /app Layout, so they were rendering with no container at all —
// full-viewport-width text on a desktop monitor, no header, no way back.
//
// Content is capped at max-w-3xl rather than the app's max-w-7xl: the app
// shell spends width on a sidebar and dense tables, while these pages are a
// single reading column and stretch badly past ~1000px.
import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function PublicLayout() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <header className="border-b-2 border-ink-900 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-2xl tracking-tight leading-none text-secondary-900">
            ONLY<span className="text-gold-700">KICKERS</span>
          </Link>

          <nav className="flex items-center gap-2 sm:gap-3">
            {/* Hidden on the narrowest screens: three nav labels wrap to two
                lines at 390px, and every page below already links to /browse. */}
            <Link
              to="/browse"
              className="hidden sm:inline text-xs font-semibold uppercase tracking-wider text-secondary-600 hover:text-ink-900 transition-colors whitespace-nowrap"
            >
              Open leagues
            </Link>
            {user ? (
              <Link
                to="/app"
                className="border border-ink-900 px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors whitespace-nowrap"
              >
                My leagues
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-xs font-semibold uppercase tracking-wider text-secondary-600 hover:text-ink-900 transition-colors whitespace-nowrap"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="bg-gold px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-gold-600 transition-colors whitespace-nowrap"
                >
                  Sign up
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-secondary-300 bg-paper-panel">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-secondary-600">
            OnlyKickers &middot; Fantasy football for kickers and punters
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-secondary-600 hover:text-ink-900 transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-xs text-secondary-600 hover:text-ink-900 transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
